export const CHUNK_LOAD_RECOVERY_STORAGE_KEY = 'sobe:chunk-load-recovery-at';
export const CHUNK_LOAD_RECOVERY_TTL_MS = 60_000;

interface ChunkLoadRecoveryOptions {
  now?: () => number;
  reload?: () => void;
  storage?: Storage | null;
  target?: Window;
}

const getSessionStorage = (target: Window): Storage | null => {
  try {
    return target.sessionStorage;
  } catch {
    return null;
  }
};

const readLastReloadAt = (storage: Storage): number | null => {
  try {
    const value = storage.getItem(CHUNK_LOAD_RECOVERY_STORAGE_KEY);
    if (!value) return null;

    const timestamp = Number(value);
    return Number.isFinite(timestamp) ? timestamp : null;
  } catch {
    return null;
  }
};

const writeLastReloadAt = (storage: Storage, timestamp: number): boolean => {
  try {
    storage.setItem(CHUNK_LOAD_RECOVERY_STORAGE_KEY, String(timestamp));
    return true;
  } catch {
    return false;
  }
};

const clearExpiredReloadMarker = (storage: Storage, now: number) => {
  const lastReloadAt = readLastReloadAt(storage);
  if (lastReloadAt === null || now - lastReloadAt < CHUNK_LOAD_RECOVERY_TTL_MS) {
    return;
  }

  try {
    storage.removeItem(CHUNK_LOAD_RECOVERY_STORAGE_KEY);
  } catch {
    // If storage becomes unavailable after the read, leave the browser error alone.
  }
};

const hasRecentlyReloaded = (storage: Storage, now: number) => {
  const lastReloadAt = readLastReloadAt(storage);
  if (lastReloadAt === null) return false;

  const age = now - lastReloadAt;
  return age >= 0 && age < CHUNK_LOAD_RECOVERY_TTL_MS;
};

export const installChunkLoadRecovery = (options: ChunkLoadRecoveryOptions = {}) => {
  const target = options.target ?? window;
  const storage = options.storage ?? getSessionStorage(target);
  if (!storage) {
    return () => {};
  }

  const now = options.now ?? Date.now;
  const reload = options.reload ?? (() => target.location.reload());

  clearExpiredReloadMarker(storage, now());

  const handlePreloadError = (event: WindowEventMap['vite:preloadError']) => {
    const timestamp = now();
    if (hasRecentlyReloaded(storage, timestamp)) {
      return;
    }

    if (!writeLastReloadAt(storage, timestamp)) {
      return;
    }

    event.preventDefault();
    reload();
  };

  target.addEventListener('vite:preloadError', handlePreloadError);

  return () => {
    target.removeEventListener('vite:preloadError', handlePreloadError);
  };
};
